// Render an iCal RRULE string as a Traditional-Chinese sentence the
// way a Taiwanese 保全 / 物業 admin would actually read it, e.g.
// "FREQ=DAILY;BYHOUR=0,1,...,23;BYMINUTE=0" → "每小時整點".
//
// Scope is the shape RRuleEditor can produce + a few common hand-edited
// variants: FREQ ∈ {DAILY, WEEKLY, MONTHLY, YEARLY} with optional
// INTERVAL / BYDAY / BYMONTHDAY / BYSETPOS / BYMONTH + BYHOUR list +
// BYMINUTE single. Malformed or out-of-scope rules fall through to the
// raw rrule string so admins still have something to diagnose with.

interface ParsedRRule {
  FREQ?: string;
  INTERVAL?: number;
  BYDAY?: string[];
  BYMONTHDAY?: number[];
  BYSETPOS?: number;
  BYMONTH?: number[];
  BYHOUR?: number[];
  BYMINUTE?: number[];
}

function parseRRule(rrule: string): ParsedRRule {
  const body = rrule.replace(/^RRULE:/, "");
  const out: ParsedRRule = {};
  for (const seg of body.split(";")) {
    const [k, v] = seg.split("=");
    if (!k || v === undefined) continue;
    switch (k) {
      case "FREQ":       out.FREQ = v; break;
      case "INTERVAL":   out.INTERVAL = Number(v); break;
      case "BYDAY":      out.BYDAY = v.split(",").filter(Boolean); break;
      case "BYMONTHDAY": out.BYMONTHDAY = v.split(",").map(Number).filter((n) => !Number.isNaN(n)); break;
      case "BYSETPOS":   out.BYSETPOS = Number(v); break;
      case "BYMONTH":    out.BYMONTH = v.split(",").map(Number).filter((n) => !Number.isNaN(n)); break;
      case "BYHOUR":     out.BYHOUR = v.split(",").map(Number).filter((n) => !Number.isNaN(n)); break;
      case "BYMINUTE":   out.BYMINUTE = v.split(",").map(Number).filter((n) => !Number.isNaN(n)); break;
    }
  }
  return out;
}

const WEEKDAY_LABEL: Record<string, string> = {
  MO: "週一", TU: "週二", WE: "週三", TH: "週四", FR: "週五", SA: "週六", SU: "週日",
};
const WEEKDAY_ORDER = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

function formatHourMinute(h: number, m: number): string {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatTimes(hours: number[] | undefined, minute: number): string {
  if (!hours || hours.length === 0) return formatHourMinute(0, minute);
  if (hours.length === 24) return minute === 0 ? "每小時整點" : `每小時 ${String(minute).padStart(2, "0")} 分`;
  const sorted = [...hours].sort((a, b) => a - b);
  return sorted.map((h) => formatHourMinute(h, minute)).join(" / ");
}

function formatWeekdays(byday: string[]): string {
  const set = new Set(byday);
  const sorted = WEEKDAY_ORDER.filter((d) => set.has(d));
  if (sorted.length === 7) return "每天";
  if (sorted.length === 5 && ["MO","TU","WE","TH","FR"].every((d) => set.has(d))) return "週一至週五";
  if (sorted.length === 2 && set.has("SA") && set.has("SU")) return "週末";
  return sorted.map((d) => WEEKDAY_LABEL[d] ?? d).join("、");
}

function ordinal(n: number): string {
  if (n === -1) return "倒數第 1 個";
  if (n < 0) return `倒數第 ${-n} 個`;
  return `第 ${n} 個`;
}

export function humanizeRRule(rrule: string): string {
  if (!rrule || !rrule.trim()) return "";
  try {
    const p = parseRRule(rrule);
    if (!p.FREQ) return rrule;

    const minute = p.BYMINUTE?.[0] ?? 0;
    const timeStr = formatTimes(p.BYHOUR, minute);
    const everyN = p.INTERVAL && p.INTERVAL > 1 ? p.INTERVAL : null;

    if (p.FREQ === "DAILY") {
      // Hourly-all-day reads cleaner without a redundant "每天" prefix.
      if (p.BYHOUR?.length === 24) return everyN ? `每 ${everyN} 天 · 每小時整點` : timeStr;
      const day = everyN ? `每 ${everyN} 天` : "每天";
      return `${day} ${timeStr}`;
    }

    if (p.FREQ === "WEEKLY") {
      const dayPart = p.BYDAY && p.BYDAY.length > 0 ? formatWeekdays(p.BYDAY) : "每週";
      const prefix = everyN ? `每 ${everyN} 週 · ${dayPart}` : dayPart;
      return `${prefix} ${timeStr}`;
    }

    if (p.FREQ === "MONTHLY") {
      const everyMonth = everyN ? `每 ${everyN} 個月` : "每月";
      if (p.BYDAY && p.BYDAY.length > 0 && p.BYSETPOS !== undefined) {
        return `${everyMonth} ${ordinal(p.BYSETPOS)} ${formatWeekdays(p.BYDAY)} ${timeStr}`;
      }
      if (p.BYMONTHDAY && p.BYMONTHDAY.length > 0) {
        return `${everyMonth} ${p.BYMONTHDAY.join(" / ")} 號 ${timeStr}`;
      }
      return `${everyMonth} ${timeStr}`;
    }

    if (p.FREQ === "YEARLY") {
      const everyYear = everyN ? `每 ${everyN} 年` : "每年";
      if (p.BYMONTH && p.BYMONTH.length > 0 && p.BYMONTHDAY && p.BYMONTHDAY.length > 0) {
        const md = `${p.BYMONTH[0]}/${p.BYMONTHDAY[0]}`;
        return `${everyYear} ${md} ${timeStr}`;
      }
      return `${everyYear} ${timeStr}`;
    }

    return rrule;
  } catch {
    return rrule;
  }
}

/** "60 分鐘任務 · 可提前 15 分鐘、延後 30 分鐘". Drops the grace clause
 *  entirely when both sides are 0 so simple tasks read cleanly. */
export function humanizeWindow(
  durationMin: number,
  graceBeforeMin: number,
  graceAfterMin: number,
): string {
  const base = `${durationMin} 分鐘任務`;
  if (graceBeforeMin <= 0 && graceAfterMin <= 0) return base;
  const parts: string[] = [];
  if (graceBeforeMin > 0) parts.push(`可提前 ${graceBeforeMin} 分鐘`);
  if (graceAfterMin > 0) parts.push(`延後 ${graceAfterMin} 分鐘`);
  return `${base} · ${parts.join("、")}`;
}
